import { useId, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { listThreads, newClientKey, readThread } from "../../../../api/threads/threadsApi";
import type { ThreadSummary } from "../../../../api/threads/threadDecoders";
import { useLoad } from "../../../../shared/loading/useLoad";
import { appPaths } from "../../../../shared/routing/appPaths";
import { useWorkspace } from "../../../../shell/workspace";
import AppButton from "../../../../ui/buttons/AppButton";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import { labelOf, useVersionLabels } from "../../../versions/labels/useVersionLabels";
import VersionLabelText from "../../../versions/labels/VersionLabelText";
import ThreadStatusBadge from "../../status/ThreadStatusBadge";
import { preparationPath } from "../intentAddress";
import type { ThreadSubject } from "../threadIntent";
import { earlierRunsOn } from "./earlierRunChoices";

interface EarlierRunsProps {
  projectId: string;
  subject: ThreadSubject;
}

/**
 * Earlier runs on this document (F30, R-7): starting fresh is the default; here the user can
 * instead continue one, or branch from one of its steps, which forks it (T-10), so the runs
 * form a tree. Shown apart from the new run, each with its Workflow version. Nothing is
 * listed when the document has no runs.
 */
export default function EarlierRuns({ projectId, subject }: EarlierRunsProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const load = useCallback((signal: AbortSignal) => listThreads(projectId, signal), [projectId]);
  const { state } = useLoad(load);
  const runs = useMemo(() => (state.status === "loaded" ? earlierRunsOn(state.value, subject) : []), [state, subject]);
  if (runs.length === 0) {
    return null;
  }
  return (
    <section className="space-y-2" aria-labelledby={headingId}>
      <h3 id={headingId} className="app-title text-sm font-semibold">{t("threads.prepare.earlier.title")}</h3>
      <p className={FORM_HELP_TEXT_CLASS}>{t("threads.prepare.earlier.hint")}</p>
      <ul className="space-y-1.5">
        {runs.map((run) => <EarlierRun key={run.threadId} projectId={projectId} run={run} />)}
      </ul>
    </section>
  );
}

function EarlierRun({ projectId, run }: { projectId: string; run: ThreadSummary }) {
  const { t } = useTranslation();
  const workspace = useWorkspace();
  const load = useCallback((signal: AbortSignal) => readThread(run.threadId, signal), [run.threadId]);
  const { state: overview } = useLoad(load);
  const workflow = overview.status === "loaded" ? overview.value.workflow : null;
  // The Workflow version by its label (ADR-0033 D1): `features/versions/` names it.
  const labels = useVersionLabels(projectId, { itemVersions: workflow === null ? [] : [workflow], collectionVersions: [] });
  const branch = () => {
    workspace.open(
      preparationPath({
        intent: { kind: "fork", projectId, returnTo: null, threadId: run.threadId, atEntry: run.lastEntryNumber },
        requestKey: newClientKey(),
      }),
    );
  };
  return (
    <li className="app-card-muted flex flex-wrap items-center gap-2 border-l-4 border-l-[var(--app-accent)] px-2 py-1.5 text-sm">
      <span className="min-w-0 flex-1 truncate" title={run.title}>{run.title}</span>
      {workflow === null ? null : (
        <VersionLabelText name={t("threads.prepare.earlier.workflow")} label={labelOf(labels, workflow)} className="app-text-muted text-xs" />
      )}
      <ThreadStatusBadge status={run.projection.display_status} compact={true} />
      <AppButton variant="secondary" size="xs" onClick={() => { workspace.open(appPaths.thread(run.threadId)); }}>
        {run.state === "closed" ? t("threads.prepare.earlier.open") : t("threads.prepare.earlier.continue")}
      </AppButton>
      <AppButton variant="secondary" size="xs" onClick={branch}>{t("threads.prepare.earlier.branch")}</AppButton>
    </li>
  );
}
